// Valid headers, compared after `normalizeHeader`. The original 45-column export
// plus the 17 columns Turning Point added on 2026-06-08 (per-TN LRN + structured
// service-address columns) = 62, plus the 2 scheduled-activation columns added
// 2026-06-21 = 64. Older 45/62-col files still validate (this is a superset;
// required headers are unchanged).
export const SIDEWINDER_MIGRATION_CSV_VALID_HEADERS = [
  'TRANSACTION_ID', 'CUSTOMER REQUEST ID', 'CUSTOMER_ID', 'ORDER_TYPE', 'PORT_TYPE',
  'DIVISION', 'PON', 'VER', 'ATN', 'TN', 'TOS', 'ACT', 'END_USER_NAME',
  'ORDER_CREATE_DATE', 'VERSION_CREATE_DATE', 'STATUS', 'SUP_TYPE', 'STATUS_DATE',
  'STATUS_USER_ID', 'CRDD', 'FOC_DATE', 'ONSP', 'ONSP_NAME', 'BONDED_FLAG', 'NNSP',
  'NNSP_NAME', 'RETURN_CODE', 'RETURN_REASON', 'ADDITIONAL_ERROR_MESSAGES',
  'CARRIER_CONNECTION_TYPE', 'Ported TN Quantity', 'Account Number', 'Authorization Name',
  'CAI', 'Remarks', 'LRN', 'AUTHORIZATION', 'LOA_DATE', 'FULL_PORT', 'DUE_DATE_TIMEZONE',
  'END_USER_LISTING_TREATMENT', 'END_USER_MOVING_INDICATOR', 'IS_ADDRESS_SAME_FOR_ALL_TNs',
  'IS_REQUEST_FROM_UI', 'ERROR_CORRECTION_LEVEL',
  // Added 2026-06-08 (45 -> 62): per-TN LRN, then structured service-address columns.
  'TN LRN',
  'SAPR', 'SANO', 'SASF', 'SASD', 'SASN', 'SATH', 'SASS',
  'LD1', 'LV1', 'LD2', 'LV2', 'LD3', 'LV3',
  'CITY', 'STATE', 'ZIP',
  // Added 2026-06-21 (62 -> 64): scheduled-activation signal. Presence of
  // ACTIVATION_DATE => scheduled (else on-demand); the time-of-day is not stored.
  'ACTIVATION_DATE', 'ACTIVATION_TIME',
] as const;

export const SIDEWINDER_MIGRATION_CSV_REQUIRED_HEADERS = ['PON', 'TN', 'STATUS'] as const;

/**
 * POR-125 SOA On-Demand CSV columns — Turning Point's SOA On-Demand Activation template.
 * Downloaded from the migrate page for migrated TP orders that still carry a scheduled
 * activation, and re-uploaded to the "Process SOA On-Demand" action, which loops each row
 * through TP's `soa/activate` (`activateNow`) to switch the order from scheduled to on-demand.
 * The order need not exist in cpaas — the switch is purely a TP call.
 */
export const SIDEWINDER_SOA_ON_DEMAND_CSV_HEADERS = [
  'Purchase Order Number',
  'Auto Activation Date',
  'Auto Activation Time',
  'Activate Now',
  'Remarks',
] as const;

export interface SidewinderMigrationCsvRow {
  // Loose JSON shape — mapping/transform happens in the follow-up story.
  [key: string]: string | undefined;
  message?: string;
  status?: 'imported' | 'failed';
}

/**
 * Sentinel key set on a staged raw CSV row (value `'true'`) to mark it as
 * originating from the supplementary TN-LRN report (the 3-col `PON, TN, TN LRN`
 * upload) rather than the base TP export. It rides inside the row's `raw` JSON,
 * so it survives the staging round-trip with no schema column. Consumed by
 * `collapseSidewinderCsvOrder` to apply EXPLICIT per-TN LRN precedence
 * (base TP export is authoritative; report rows only fill TNs the base left
 * empty) instead of relying on incidental row sort order.
 */
export const LRN_REPORT_ROW_FLAG = '__lrnReportRow';

export interface SidewinderMigrationCsvUploadResponse {
  success: boolean;
  imported: SidewinderMigrationCsvRow[];
  existed: SidewinderMigrationCsvRow[]; // always [] in POR-33
  failed: SidewinderMigrationCsvRow[];
  errorMessage?: string;
}
